import {put, call, all} from 'redux-saga/effects';
import InvoiceBatch from "../../entities/invoiceBatch";
import * as Actions from "../actions/invoiceBatchActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type InvoiceBatchAction = {
    invoiceBatchId: string;
    invoiceBatch: InvoiceBatch;
    invoiceBatches: InvoiceBatch[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneInvoiceBatch(action: InvoiceBatchAction) {
	const session = tabSession.captureGeneration();
	try {
		const invoiceBatch: InvoiceBatch = yield call(api.findOne, 'invoiceBatch', action.invoiceBatchId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_FETCH_ONE_SUCCESS, invoiceBatchId: action.invoiceBatchId, invoiceBatch, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCH_FETCH_ONE_FAILURE, invoiceBatchId: action.invoiceBatchId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {InvoiceBatchAction} action
 */
export function* saveInvoiceBatch(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ INVOICEBATCH_SAVE saga', action.invoiceBatch?.id);

		if (action.invoiceBatch === undefined || action.invoiceBatch === null) {
			console.error('saveInvoiceBatch received a null');
			throw new Error('saveInvoiceBatch received a null');
		}

		if (!(action.invoiceBatch instanceof InvoiceBatch)) {
			action.invoiceBatch = new InvoiceBatch(action.invoiceBatch);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.invoiceBatch.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.invoiceBatch);
			const addResult = yield call(idb.add, 'invoiceBatch', action.invoiceBatch);
			if (addResult.isErr()) {
				console.error(`⮂ saveInvoiceBatch: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertInvoiceBatchSave(action.invoiceBatch, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.INVOICEBATCH_SAVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.INVOICEBATCH_SAVE, invoiceBatch: action.invoiceBatch, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertInvoiceBatchSave(action.invoiceBatch, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.INVOICEBATCH_SAVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {InvoiceBatchAction} action
 * @param {InvoiceBatch[]} action.invoiceBatches
 */
export function* saveManyInvoiceBatches(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ INVOICEBATCH_SAVE_MANY saga', action.invoiceBatches.map(invoiceBatch => invoiceBatch.id).join(','));

        if (!Array.isArray(action.invoiceBatches)) {
            console.error('saveManyInvoiceBatches expected an array, received', action.invoiceBatches);
            throw new Error('saveManyInvoiceBatches expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveInvoiceBatch), under these queue keys
		const dispatchedAddedOns: number[] = action.invoiceBatches.map(invoiceBatch => invoiceBatch?.addedOn);
		const revertAll = () => action.invoiceBatches.map((invoiceBatch, i) =>
			put(Actions.revertInvoiceBatchSave(invoiceBatch, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: InvoiceBatch[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.invoiceBatches.length; i++) {
				const invoiceBatch = action.invoiceBatches[i];
				invoiceBatch.persisted = false;
				ensureOperationId(invoiceBatch);
				toSave.push(invoiceBatch);
			}

			const addResult = yield call(idb.add, 'invoiceBatch', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyInvoiceBatches: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.INVOICEBATCH_SAVE_MANY_FAILURE, invoiceBatches: action.invoiceBatches, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.INVOICEBATCH_SAVE_MANY, invoiceBatches: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.INVOICEBATCH_SAVE_MANY_FAILURE, invoiceBatches: action.invoiceBatches, status: 500, err});

			console.error(`Error saving ${action.invoiceBatches.length} invoiceBatches to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {InvoiceBatchAction} action
 */
export function* removeInvoiceBatch(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.invoiceBatch === undefined || action.invoiceBatch === null || !(action.invoiceBatch instanceof InvoiceBatch)) {
			console.error('removeInvoiceBatch expected type InvoiceBatch, received', action.invoiceBatch);
			reportQueued(false);
			throw new Error('removeInvoiceBatch expected type InvoiceBatch');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.invoiceBatch.status = 'D';
		action.invoiceBatch.addedOn = newHRTimestamp();
		action.invoiceBatch.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.invoiceBatch);
		const addResult = yield call(idb.add, 'invoiceBatch', action.invoiceBatch);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeInvoiceBatch: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.INVOICEBATCH_REMOVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_REMOVE, invoiceBatch: action.invoiceBatch});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCH_REMOVE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this InvoiceBatch.
	*
	* @param {InvoiceBatchAction} action
	*/
export function* restoreInvoiceBatch(action: InvoiceBatchAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreInvoiceBatch expected getCurrentUserId() to return a value');
        return;
    }

	if (action.invoiceBatch === undefined || action.invoiceBatch === null) {
        console.error('restoreInvoiceBatch expected a InvoiceBatch, received null');
        throw new Error('restoreInvoiceBatch expected a InvoiceBatch, received null');
    }
    else if (!(action.invoiceBatch instanceof InvoiceBatch)) action.invoiceBatch = new InvoiceBatch(action.invoiceBatch); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.invoiceBatch.status = 'U';
		action.invoiceBatch.addedOn = newHRTimestamp();
		action.invoiceBatch.addedBy = userId;
		action.invoiceBatch.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.invoiceBatch);
		const addResult = yield call(idb.add, 'invoiceBatch', action.invoiceBatch);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreInvoiceBatch: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCH_RESTORE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCH_RESTORE, invoiceBatch: action.invoiceBatch});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.INVOICEBATCH_RESTORE_FAILURE, invoiceBatch: action.invoiceBatch, status: 500, err});

		console.error('Error restoring from database', err);
	}
}